// apps/account-portal/src/pages/GamesPage.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import PageFooter from '../components/PageFooter';
import PageContainer from '../components/PageContainer';
import Card from '../components/Card';

import DarkCraftIcon from '../assets/icons/DarkCraft.webp';
import ReversiRealmsIcon from '../assets/icons/ReversiRealms.webp';

const PLAY_STORE_DEV_URL = 'https://play.google.com/store/apps/dev?id=6670731122014880122';

const PlayStoreIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.18 23.76c.3.17.64.24.99.2l12.4-11.86L13.23 8.7 3.18 23.76zM.32 1.5A1.64 1.64 0 000 2.5v19c0 .36.11.69.32.97l.1.1 10.64-10.63v-.25L.42 1.4l-.1.1zM20.47 10.4l-2.92-1.66-3.32 3.26 3.32 3.27 2.95-1.68c.84-.48.84-1.71-.03-2.19zM4.17.24l12.4 11.86-3.34 3.34L.99.44A1.19 1.19 0 014.17.24z"/>
  </svg>
);

const gameKeys = [
  { key: 'darkcraft', icon: DarkCraftIcon, storeUrl: 'https://play.google.com/store/apps/details?id=com.redpandatech.darkcraft' },
  { key: 'reversirealms', icon: ReversiRealmsIcon, storeUrl: 'https://play.google.com/store/apps/details?id=com.redpandatech.reversirealms' },
];

const GamesPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      {/* 標題列 */}
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
          {t('navbar.games')}
        </h1>
        <a
          href={PLAY_STORE_DEV_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 text-[var(--accent)] hover:underline text-sm font-medium whitespace-nowrap"
        >
          {t('apps.dev_page_link')}
        </a>
      </div>

      {/* 遊戲卡片 */}
      <div className="grid gap-5 mb-12" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {gameKeys.map(({ key, icon, storeUrl }) => (
          <Card
            key={key}
            className="hover:[box-shadow:var(--card-shadow-hover)] transition-shadow duration-200 flex flex-col h-full"
          >
            {/* 圖示 + 名稱 */}
            <div className="flex items-center mb-4 gap-3.5">
              <div
                className="w-[46px] h-[46px] rounded-[13px] flex-shrink-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${icon})` }}
                role="img"
                aria-label={key}
              />
              <div>
                <h3 className="text-[17px] font-bold text-[var(--text-primary)] leading-tight">
                  {t(`games.${key}.name`)}
                </h3>
                <p className="text-[13px] text-[var(--text-muted)]">{t(`games.${key}.subtitle`)}</p>
              </div>
            </div>

            {/* 說明 + 模式（flex-1 讓按鈕固定在底部）*/}
            <div className="mb-6 flex-1 flex flex-col">
              <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
                {t(`games.${key}.description`)}
              </p>
              <div className="mt-auto">
                <div className="flex flex-col gap-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-start gap-2 text-sm text-[var(--text-secondary)] text-balance">
                      <svg width="13" height="13" viewBox="0 0 20 20" className="flex-shrink-0 mt-1">
                        <polyline points="4,10 8,14 16,6" fill="none" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {t(`games.${key}.mode${i}`)}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Google Play 按鈕 */}
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[var(--accent-fill)] text-[var(--accent-on)] hover:opacity-90 font-semibold py-2.5 px-4 rounded-md transition-opacity duration-150 text-sm"
            >
              <PlayStoreIcon className="w-4 h-4" />
              Google Play
            </a>
          </Card>
        ))}
      </div>

      <PageFooter />
    </PageContainer>
  );
};

export default GamesPage;
